import StatCard from "./components/StatCard";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { DashboardService } from "@/services/dashboardService";
import CandidateStatusService from "@/services/candidateStatusService";
import PipelineCard from "./components/PipelineCard";
import { Loader2 } from "lucide-react";
import { MatchBar } from "@/components/ui/MatchBar";
import { Badge } from "@/components/ui/Badge";
import { useOutletContext, useNavigate } from "react-router-dom";
import { formatDate } from "@/utils/dateUtils";
import DataTable from "@/components/shared/DataTable";
import { Button, ButtonShadcn } from "@/components/ui/Button";
import useLiveResource from "@/hooks/useLiveResource";
import { Filter, FilterPill } from "@/components/shared/Filter";
import { JobService } from "@/services/jobService";
import MatchScoreLegend from "./components/MatchScoreLegend";
import { EMPLOYEE_TYPE } from "@/config/dropdownData";
import { hasPermission } from "@/permissions/rbac";
import { PERMISSIONS } from "@/permissions/constant";
import { useAuth } from "@/hooks/useAuth";
import PermissionGuard from "@/permissions/PermissionGuard";

const BC_CHANNEL = "candidate_sync";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [flows, setFlows] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [totalCandidates, setTotalCandidates] = useState(null);

  const [candidateStatuses, setCandidateStatuses] = useState([]);
  // Analysing / done / failed, read off the event's name. The score it used to
  // sit beside — `scoreOverrides`, fed from `match_score` — is gone: the row is
  // re-read instead.
  const [statusOverridesMatch, setStatusOverridesMatch] = useState({});
  const [anyProcessing, setAnyProcessing] = useState(false);
  const [topJobs, setTopJobs] = useState([]);

  const candidatesRef = useRef(candidates);
  const statusOverridesMatchRef = useRef(statusOverridesMatch);
  const selectedJobIdRef = useRef(selectedJobId);
  const selectedFlowIdRef = useRef(selectedFlowId);
  // The flow whose statuses `pipeline_breakdown` is scoped to — the selected
  // job's flow, or the tenant's Default flow when no job is selected. Kept
  // in a ref too so the WebSocket handlers below (registered once) always
  // read the current value instead of the one from their first render.
  const scopedFlowIdRef = useRef(null);
  const bcRef = useRef(null);
  // The tenant-wide socket and BroadcastChannel can carry the same confirmation.
  // The rows an event said had moved since the last refetch, so the cross-tab
  // message names them rather than carrying their new values.
  const touchedApplicationsRef = useRef(new Set());

  const hasViewDashboardStats = hasPermission(PERMISSIONS.DASHBOARD_VIEW, user.permissions);
  const hasViewCandidates = hasPermission(PERMISSIONS.CANDIDATE_VIEW, user.permissions);
  const hasViewJobs = hasPermission(PERMISSIONS.JOB_VIEW, user.permissions);

  const getAppKey = (appId, jobId) => `${appId}_${jobId}`;

  useEffect(() => {
    candidatesRef.current = candidates;
  }, [candidates]);

  useEffect(() => {
    statusOverridesMatchRef.current = statusOverridesMatch;
  }, [statusOverridesMatch]);

  useEffect(() => {
    selectedJobIdRef.current = selectedJobId;
  }, [selectedJobId]);

  useEffect(() => {
    selectedFlowIdRef.current = selectedFlowId;
  }, [selectedFlowId]);

  useEffect(() => {
    scopedFlowIdRef.current = stats?.scoped_flow_id ?? null;
  }, [stats?.scoped_flow_id]);

  const setHeader = useOutletContext();
  useEffect(() => {
    setHeader({ title: "Dashboard", subTitle: "Recruitment pipeline overview" });
  }, [setHeader]);

  const normalizeStatus = (status = {}) => ({
    id: status?.id ?? "",
    code: status?.code ?? "",
    name: status?.name ?? "",
    description: status?.description ?? "",
    category: status?.category ?? "",
    isInitial: !!status?.isInitial,
    isHired: !!status?.isHired,
    isRejected: !!status?.isRejected,
    isDefault: !!status?.isDefault,
    isActive: status?.isActive !== false,
    sequence: status?.sequence ?? 9999,
  });

  const normalizeStatusValue = (status) => {
    if (!status) return "";
    if (typeof status === "string") return status.trim();
    return status?.name || status?.label || status?.code || status?.status || "";
  };

  const normalizeStatusKey = (value) => {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[_-]+/g, "");
  };

  const getBreakdownValue = (status, breakdown = {}) => {
    const candidates = [
      status?.id,
      status?.code,
      status?.name,
      status?.category,
      status?.code?.toLowerCase?.(),
      status?.name?.toLowerCase?.(),
      status?.category?.toLowerCase?.(),
      status?.code?.replace(/\s+/g, "_")?.toLowerCase?.(),
      status?.name?.replace(/\s+/g, "_")?.toLowerCase?.(),
      status?.category?.replace(/\s+/g, "_")?.toLowerCase?.(),
    ].filter(Boolean);

    for (const key of candidates) {
      if (breakdown?.[key] != null) return breakdown[key];
    }

    const normalizedEntries = Object.entries(breakdown || {});
    for (const candidateKey of candidates) {
      const normalizedCandidate = normalizeStatusKey(candidateKey);
      const matched = normalizedEntries.find(([k]) => normalizeStatusKey(k) === normalizedCandidate);
      if (matched) return matched[1];
    }

    return 0;
  };

  // Statuses are tenant-configurable now (custom names/order via the
  // candidate-status admin page), so colors can't be guessed from hardcoded
  // English names ("shortlisted", "interviewed", ...) — that breaks the
  // moment a status is renamed or a custom one is added. Match the job-detail
  // page's pipeline bar instead: color is just the status's position in the
  // (already sequence-sorted) list, cycled through PIPELINE_ACCENTS.
  const getStatusColorIndex = (_status, index = 0) => index;

  const fetchTopJobs = async () => {
    try {
      const jobsRes = await JobService.fetchAdminJobDetails({
        perPage: 5,
        top_job: true,
      });

      const jobList = jobsRes?.data?.jobs ?? jobsRes?.data ?? jobsRes?.jobs ?? [];
      setTopJobs(jobList.slice(0, 5));
    } catch (err) {
      console.error("Failed to refresh top jobs:", err);
    }
  };

  // Every non-deleted flow, active or not — this lets a recruiter look back
  // at a flow's breakdown even after it's been retired from new job
  // selection, not just the ones currently offered on the job form.
  const fetchFlows = async () => {
    try {
      const res = await CandidateStatusService.listFlows({ per_page: 100 });
      const rawFlows = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
      setFlows(
        rawFlows.map((f) => ({
          id: f?.id ?? "",
          name: f?.name ?? "",
          isDefault: !!f?.isDefault,
        }))
      );
    } catch (err) {
      console.error("Failed to fetch candidate status flows:", err);
      setFlows([]);
    }
  };

  // Scoped to the same flow `pipeline_breakdown` is scoped to (the tenant's
  // Default flow, or the selected job's flow) — otherwise this returns every
  // flow's statuses and the pipeline tiles mix them all together.
  const fetchCandidateStatuses = async (flowId = scopedFlowIdRef.current) => {
    try {
      const res = await CandidateStatusService.list({
        page: 1,
        per_page: 100,
        sort_by: "name",
        sort_order: "asc",
        flowId,
      });

      const rawStatuses = res?.data ?? res?.data?.data ?? [];

      const normalized = Array.isArray(rawStatuses) ? rawStatuses.map(normalizeStatus) : [];

      normalized.sort((a, b) => {
        const aOrder = a.sequence ?? 9999;
        const bOrder = b.sequence ?? 9999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a.name).localeCompare(String(b.name));
      });

      setCandidateStatuses(normalized);
    } catch (err) {
      console.error("Failed to fetch candidate statuses:", err);
      setCandidateStatuses([]);
    }
  };

  const loadData = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      const [statsRes, candidatesRes, jobsRes] = await Promise.all([
        hasViewDashboardStats ? DashboardService.fetDashboardStats("") : Promise.resolve(null),
        hasViewCandidates
          ? DashboardService.fetCandidateList({
            params: { sort_by: "applied_at", sort_order: "desc", per_page: 5 },
          })
          : Promise.resolve(null),
        hasViewJobs ? JobService.fetchAdminJobDetails({ perPage: 5, top_job: true }) : Promise.resolve(null),
      ]);

      setStats(statsRes?.data);
      setTotalCandidates(statsRes?.data?.total_candidates ?? null);
      setJobs(statsRes?.data?.open_jobs ?? []);

      await fetchCandidateStatuses(statsRes?.data?.scoped_flow_id ?? null);

      const rows = candidatesRes?.data?.candidates ?? candidatesRes?.data ?? [];
      setCandidates(rows);

      setTopJobs(jobsRes?.data?.jobs?.slice(0, 5) ?? []);

      const appKeys = new Set(rows.map((c) => getAppKey(c.application_id, c.job_id)));

      setStatusOverridesMatch((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => appKeys.has(k))));

      setAnyProcessing(
        rows.some((c) => {
          const appKey = getAppKey(c.application_id, c.job_id);
          const status = statusOverridesMatchRef.current[appKey] ?? c.match_status;
          return status === "IN_PROGRESS";
        }),
      );
    } catch (e) {
      setError(e.message ?? "Failed to load dashboard.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  /**
   * The dropdown, refreshed from a stats payload — and the selection dropped
   * when the job behind it is no longer on offer.
   *
   * `open_jobs` rides on every stats response, and every job event already
   * triggers a refetch, so the list can be current for free. Without this the
   * dropdown was whatever the first load returned: a job closed, deleted or
   * expired elsewhere stayed on offer, and selecting it filtered the board
   * down to a job the rest of the product treats as gone — the pill losing
   * its title and reading just "Job".
   */
  const syncJobFilter = (openJobs) => {
    if (!Array.isArray(openJobs)) return;
    setJobs(openJobs);
    setSelectedJobId((current) =>
      current && !openJobs.some((job) => job.id === current) ? "" : current,
    );
  };

  const refetchStats = async ({ silent = false } = {}) => {
    try {
      if (!silent) setStatsLoading(true);

      const statsRes = hasViewDashboardStats ? await DashboardService.fetDashboardStats(selectedJobIdRef.current, selectedFlowIdRef.current) : null;

      setStats((prev) => ({
        ...prev,
        pipeline_breakdown: statsRes?.data?.pipeline_breakdown,
        total_open_jobs: statsRes?.data?.total_open_jobs,
        total_candidates: statsRes?.data?.total_candidates ?? prev?.total_candidates,
        hired_this_month: statsRes?.data?.hired_this_month ?? prev?.hired_this_month,
        scoped_flow_id: statsRes?.data?.scoped_flow_id ?? prev?.scoped_flow_id,
        scoped_flow_name: statsRes?.data?.scoped_flow_name ?? prev?.scoped_flow_name,
      }));

      syncJobFilter(statsRes?.data?.open_jobs);
    } catch (_) {
    } finally {
      if (!silent) setStatsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    fetchFlows();
  }, []);

  useEffect(() => {
    if (loading) return;

    const run = async () => {
      try {
        setStatsLoading(true);
        setError(null);

        const statsRes = hasViewDashboardStats ? await DashboardService.fetDashboardStats(selectedJobId, selectedFlowId) : null;

        setStats((prev) => ({
          ...prev,
          pipeline_breakdown: statsRes?.data?.pipeline_breakdown,
          total_open_jobs: statsRes?.data?.total_open_jobs ?? prev?.total_open_jobs,
          total_candidates: statsRes?.data?.total_candidates ?? prev?.total_candidates,
          hired_this_month: statsRes?.data?.hired_this_month ?? prev?.hired_this_month,
          scoped_flow_id: statsRes?.data?.scoped_flow_id ?? null,
          scoped_flow_name: statsRes?.data?.scoped_flow_name ?? null,
        }));

        await fetchCandidateStatuses(statsRes?.data?.scoped_flow_id ?? null);
        syncJobFilter(statsRes?.data?.open_jobs);
      } catch (e) {
        setError(e.message ?? "Failed to load stats.");
      } finally {
        setStatsLoading(false);
      }
    };

    run();
  }, [selectedJobId, selectedFlowId]);

  /** The five most recent candidates, re-read. */
  const fetchCandidateSection = useCallback(async () => {
    if (!hasViewCandidates) return;
    try {
      const res = await DashboardService.fetCandidateList({
        params: { sort_by: "applied_at", sort_order: "desc", per_page: 5 },
      });
      setCandidates(res?.data?.candidates ?? res?.data ?? []);
    } catch (err) {
      // The section keeps what it has; the next event or reconnect asks again.
      console.error("Failed to refresh the candidate section:", err);
    }
  }, [hasViewCandidates]);

  // ── The cross-tab channel (§4.5) ─────────────────────────────────────────
  //
  // Every message is a key, never a value: `application:<id>` for one row, or
  // `table` for the list itself. This screen shows a top-five, so either one
  // means the same thing — re-read the section — and the receiving tab reads
  // the server rather than trusting what the sending tab painted.
  useEffect(() => {
    bcRef.current = new BroadcastChannel(BC_CHANNEL);

    let timer = null;
    bcRef.current.onmessage = (e) => {
      if (!e.data?.key) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        fetchCandidateSection();
        refetchStats({ silent: true });
      }, 200);
    };

    return () => {
      clearTimeout(timer);
      bcRef.current?.close();
      bcRef.current = null;
    };
  }, [fetchCandidateSection]);

  const refreshJobsSection = () => {
    fetchTopJobs();
    refetchStats({ silent: true });
  };

  // Three subscriptions, one per section: a job event must not refetch the
  // candidate list, and a candidate event must not refetch the job list.

  // The jobs section. `job.updated` is the one that can have renamed a job,
  // and a job's title also shows in the candidate rows' "Applied for" column —
  // the title is not on the envelope, so that section is re-read too. Opening
  // or closing a job cannot change a title, and leaves the candidates alone.
  const jobRenamedRef = useRef(false);
  useLiveResource({
    keys: "job",
    onEvent: (event) => {
      if (event.event === "job.updated") jobRenamedRef.current = true;
    },
    onChange: () => {
      refreshJobsSection();
      if (jobRenamedRef.current) {
        jobRenamedRef.current = false;
        fetchCandidateSection();
      }
    },
  });

  // The candidate section.
  useLiveResource({
    keys: ["match", "application", "candidate", "bulk_batch"],
    onEvent: (event) => {
      const applicationId = event.parent?.application ?? (event.resource === "application" ? event.id : null);
      const jobId = event.parent?.job;
      if (applicationId) touchedApplicationsRef.current.add(String(applicationId));
      if (event.resource !== "match" || !applicationId || !jobId) return;

      const appKey = getAppKey(applicationId, jobId);
      if (event.event === "match.started") {
        setAnyProcessing(true);
        setStatusOverridesMatch((prev) => ({ ...prev, [appKey]: "IN_PROGRESS" }));
        return;
      }
      setStatusOverridesMatch((prev) => ({
        ...prev,
        [appKey]: event.event === "match.ready" ? "COMPLETED" : "FAILED",
      }));
    },
    onChange: () => {
      fetchCandidateSection();
      refetchStats({ silent: true });
      // One key per row that moved — the other tabs read the server themselves.
      const touched = [...touchedApplicationsRef.current];
      touchedApplicationsRef.current.clear();
      if (touched.length === 0) {
        bcRef.current?.postMessage({ key: "table" });
      } else {
        touched.forEach((applicationId) => bcRef.current?.postMessage({ key: `application:${applicationId}` }));
      }
    },
  });

  // The pipeline card's own axis: the tenant's list of statuses.
  useLiveResource({
    keys: "candidate_status",
    onChange: () => {
      fetchCandidateStatuses();
      refetchStats({ silent: true });
    },
  });

  const recent = candidates.slice(0, 5).map((c) => {
    const appKey = getAppKey(c.application_id, c.job_id);
    return {
      id: c.id,
      application_id: c.application_id,
      candidate: c.full_name,
      job: c.job_title,
      matchScore: c.match_score != null ? c.match_score / 10 : null,
      matchStatus: statusOverridesMatch[appKey] ?? c.match_status,
      status: c.status,
      appliedDate: formatDate(c.applied_at),
      jobId: c.job_id,
    };
  });

  const JOB_COLUMNS = [
    {
      key: "title",
      label: "Job Title",
      sortable: true,
      render: (val) => (
        <div className="flex flex-col items-start gap-0.5">
          <span className="text-left font-medium p-0 text-[12px]">{val}</span>
        </div>
      ),
    },
    {
      key: "employee_type",
      label: "Type",
      sortable: true,
      render: (val) => {
        const employeeType = EMPLOYEE_TYPE.find((item) => item.value === val);
        return <span className="text-text-primary text-[12px]">{employeeType?.label ?? "—"}</span>;
      },
    },
    {
      key: "vacancy",
      label: "Vacancy",
      sortable: true,
      render: (val) => <span className="text-text-primary text-[12px] font-medium">{val ?? "—"}</span>,
    },
    {
      key: "hired",
      label: "Hired",
      sortable: true,
      render: (val) => <span className="text-text-primary text-[12px] font-medium">{val ?? "—"}</span>,
    },
    {
      key: "deadline",
      label: "Deadline",
      sortable: true,
      render: (val) => <span className="text-text-primary text-[12px]">{val ? formatDate(val) : "—"}</span>,
    },
    {
      key: "job_priority",
      label: "Priority",
      sortable: true,
      render: (val) => (
        <Badge
          label={val ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : "—"}
          badgeStyle="text-[11px]"
          variant="gradient"
          colorIndex={val?.toLowerCase() === "high" ? 6 : val?.toLowerCase() === "medium" ? 7 : val?.toLowerCase() === "low" ? 3 : 0}
          className="h-6 border-[1px] w-18 text-center rounded-[2rem]"
        />
      ),
    },
  ];

  const jobRows = topJobs.map((j) => ({
    id: j.id,
    title: j.title ?? j.job_title ?? "—",
    employee_type: j.employee_type ?? null,
    vacancy: j.remaining_vacancies ?? null,
    hired: j.hired_count ?? null,
    deadline: j.deadline ?? j.dead_line ?? null,
    job_priority: j.job_priority ?? null,
  }));

  const safeNum = (val) => (val != null && typeof val !== "object" ? val : null);

  // Both candidate cards name the rule and let the API resolve it. Which
  // status counts as hired is the `is_hired` flag on the tenant's own status
  // row, and the numbers beside these labels are already counted from that
  // flag server-side — so guessing the hired status id here and sending it as
  // `status_id` could only disagree with the number it sits under. It did:
  // the guess also matched a status merely *coded* "HIRED" with the flag off.
  const STAT_ITEMS = [
    {
      label: "Open Positions",
      value: safeNum(stats?.total_open_jobs),
      route: "/admin/jobs",
      query: { status: "OPEN" },
    },
    {
      label: "Total Candidates",
      value: safeNum(totalCandidates),
      route: "/admin/candidates",
      query: { exclude_hired: true },
    },
    {
      label: "Hired This Month",
      value: safeNum(stats?.hired_this_month),
      route: "/admin/candidates",
      query: { hired_this_month: true },
    },
  ];

  const pipelineItems = useMemo(() => {
    const breakdown = stats?.pipeline_breakdown ?? {};

    return candidateStatuses
      .filter((status) => status?.isActive !== false)
      .map((status, index) => ({
        id: status.id,
        label: status.name || status.code || "Unknown",
        value: getBreakdownValue(status, breakdown),
        colorIdx: getStatusColorIndex(status, index),
        sequence: status.sequence ?? 9999,
      }))
      .sort((a, b) => a.sequence - b.sequence);
  }, [candidateStatuses, stats?.pipeline_breakdown]);

  const statusMetaMap = useMemo(() => {
    const map = new Map();

    candidateStatuses.forEach((status, index) => {
      const meta = {
        label: status?.name || status?.code || "—",
        colorIndex: getStatusColorIndex(status, index),
      };

      const keys = [status?.id, status?.code, status?.name, normalizeStatusKey(status?.id), normalizeStatusKey(status?.code), normalizeStatusKey(status?.name)].filter(
        Boolean,
      );

      keys.forEach((key) => {
        map.set(String(key), meta);
      });
    });

    return map;
  }, [candidateStatuses]);

  const jobOptions = jobs.map((job) => ({
    value: job.id,
    label: job.title,
  }));

  const flowOptions = flows.map((flow) => ({
    value: flow.id,
    label: flow.isDefault ? `${flow.name} (Default)` : flow.name,
  }));

  // Job and flow are mutually exclusive views of the same breakdown — a job
  // already pins one specific flow, so picking one clears the other rather
  // than leaving a stale, overridden filter showing as "active".
  const clearScopeFilters = () => {
    setSelectedJobId("");
    setSelectedFlowId("");
  };

  const filterConfig = [
    {
      key: "job",
      type: "select",
      label: "Active Jobs",
      value: selectedJobId,
      fullWidth: true,
      onChange: (val) => {
        setSelectedJobId(val);
        if (val) setSelectedFlowId("");
        setShowFilters(false);
      },
      options: jobOptions,
    },
    // Only worth offering once there's more than one flow to choose between.
    ...(flows.length > 1
      ? [
        {
          key: "flow",
          type: "select",
          label: "Pipeline",
          value: selectedFlowId,
          fullWidth: true,
          onChange: (val) => {
            setSelectedFlowId(val);
            if (val) setSelectedJobId("");
            setShowFilters(false);
          },
          options: flowOptions,
        },
      ]
      : []),
  ];

  const activeFilters = selectedJobId
    ? [
        (() => {
          const selected = jobs.find((j) => j.id === selectedJobId);
          return {
            key: "job",
            label: selected ? selected.title : "Job",
          };
        })(),
      ]
    : selectedFlowId
      ? [
          (() => {
            const selected = flows.find((f) => f.id === selectedFlowId);
            return {
              key: "flow",
              label: selected ? `Pipeline: ${selected.name}` : "Pipeline",
            };
          })(),
        ]
      : [];

  const COLUMNS = [
    {
      key: "candidate",
      label: "Candidate",
      sortable: true,
      render: (val, row) => (
        <Button
          onClick={() => navigate(`/admin/candidate-analysis/${row.id}?job_id=${row.jobId}`)}
          className="text-left hover:text-text-blue font-medium p-0 text-[13px]"
        >
          {val}
        </Button>
      ),
    },
    {
      key: "job",
      label: "Job",
      sortable: true,
      render: (val) => (
        <div className="flex flex-col items-start gap-0.5">
          <span className="text-[13px]">{val}</span>
        </div>
      ),
    },
    {
      key: "matchScore",
      label: "Match Indicator",
      sortable: true,
      render: (val, row) => <MatchBar score={val} status={row.matchStatus} />,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (val) => {
        const raw = normalizeStatusValue(val);
        const normalized = normalizeStatusKey(raw);

        const meta = statusMetaMap.get(String(raw)) ||
          statusMetaMap.get(normalized) || {
            label: raw || "—",
            colorIndex: 0,
          };

        return (
          <Badge
            label={meta.label}
            badgeStyle="text-[11px]"
            variant="gradient"
            colorIndex={meta.colorIndex}
            className="h-6 border-[1px] min-w-[110px] max-w-full whitespace-nowrap text-center rounded-[2rem]"
          />
        );
      },
    },
    {
      key: "appliedDate",
      label: "Applied Date",
      sortable: true,
      render: (val) => <span className="text-text-primary text-[12px]">{val}</span>,
    },
  ];

  const SectionHeader = ({ title, right, badge }) => (
    <div className="flex items-center justify-between px-4 py-3 border-b border-border-primary">
      <div className="flex items-center gap-2">
        <p className="text-[13px] font-semibold text-text-primary m-0">{title}</p>
        {badge}
      </div>
      {right}
    </div>
  );

  const ViewAllBtn = ({ onClick }) => (
    // `size="sm"` rounds itself via `var(--radius-md)`, a custom property this
    // app never defines (only `--radius` exists) — the arbitrary value goes
    // invalid and the button renders with square corners. `rounded-lg` here
    // overrides it back to the working token (tailwind-merge keeps the last
    // radius class), the same fix BrandingActionButton uses.
    <ButtonShadcn variant="outline" size="sm" onClick={onClick} className="rounded-lg">
      View All
    </ButtonShadcn>
  );

  return (
    <div className="flex flex-col gap-3" style={{ zoom: 0.95 }}>
      {error && <div className="px-3 py-2 rounded-lg text-[12px] bg-red-100 border border-red-300 text-red-400">{error}</div>}

      <PermissionGuard permission={PERMISSIONS.DASHBOARD_VIEW} isComponent>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-3">
          {STAT_ITEMS.map((s, i) => (
            <StatCard key={s.label} label={s.label} value={loading ? "…" : (s.value ?? "—")} metaIdx={i} loading={loading} route={s.route} query={s.query} />
          ))}
        </div>
      </PermissionGuard>

      <PermissionGuard permission={PERMISSIONS.CANDIDATE_VIEW} isComponent>
        <div className="rounded-xl border border-border-primary bg-background-secondary">
          <SectionHeader
            title="Candidate Pipeline Breakdown"
            badge={
              <>
                {stats?.scoped_flow_name && !selectedFlowId && (
                  <span
                    className="px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{
                      background: "#EEF2FF",
                      color: "#4F46E5",
                    }}
                  >
                    {stats.scoped_flow_name}
                  </span>
                )}
                {activeFilters.length > 0 && <FilterPill label={activeFilters[0].label} onRemove={clearScopeFilters} />}
                {statsLoading && <Loader2 size={12} className="animate-spin text-text-secondary" />}
              </>
            }
            right={
              <div className="relative">
                <PermissionGuard permission={PERMISSIONS.JOB_VIEW} isComponent>
                  <Filter
                    filters={filterConfig}
                    activeFilters={activeFilters}
                    showFilters={showFilters}
                    onToggle={() => setShowFilters((prev) => !prev)}
                    onClearAll={clearScopeFilters}
                    renderActiveFilter={(f) => <FilterPill key={f.key} label={f.label} onRemove={clearScopeFilters} />}
                  />
                </PermissionGuard>
              </div>
            }
          />
          {pipelineItems.length === 0 && !loading ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-text-secondary">
              {stats?.scoped_flow_name ? `No statuses configured on the ${stats.scoped_flow_name} flow yet.` : "No candidate statuses to show."}
            </div>
          ) : (
            <div
              className={`p-4 grid gap-3 ${
                pipelineItems.length <= 4
                  ? "grid-cols-2 md:grid-cols-2 xl:grid-cols-4"
                  : pipelineItems.length <= 6
                    ? "grid-cols-2 md:grid-cols-3 xl:grid-cols-6"
                    : "grid-cols-2 md:grid-cols-3 xl:grid-cols-7"
              }`}
            >
              {pipelineItems.map((item, i) => (
                <PipelineCard key={item.id || item.label} label={item.label} value={item.value} colorIdx={item.colorIdx ?? i} loading={loading} />
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border-primary bg-background-secondary">
          <SectionHeader
            title="Recent Applications"
            badge={
              anyProcessing && !loading ? (
                <div
                  className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                  style={{
                    background: "rgba(99,102,241,0.1)",
                    border: "1px solid rgba(99,102,241,0.2)",
                    color: "#818cf8",
                  }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#818cf8] inline-block" />
                  Processing
                </div>
              ) : null
            }
            right={
              <div className="flex items-center gap-3">
                <div className="hidden md:flex items-center gap-2 text-[11px] text-text-secondary">
                  <MatchScoreLegend />
                </div>
                <ViewAllBtn onClick={() => navigate("/admin/candidates")} />
              </div>
            }
          />

          {loading ? (
            <div className="p-4">
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="grid grid-cols-5 items-center gap-4 py-1.5">
                    <div className="flex flex-col gap-1">
                      <div className="h-3.5 w-28 rounded bg-gray-200/60 animate-pulse" />
                      <div className="h-3 w-20 rounded bg-gray-200/40 animate-pulse" />
                    </div>
                    <div className="h-3.5 w-24 rounded bg-gray-200/60 animate-pulse" />
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-20 rounded-full bg-gray-200/60 animate-pulse" />
                      <div className="h-3 w-7 rounded bg-gray-200/40 animate-pulse" />
                    </div>
                    <div className="h-5 w-18 rounded-full bg-gray-200/50 animate-pulse" />
                    <div className="h-3.5 w-14 ml-auto rounded bg-gray-200/60 animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <DataTable key="recent-table" columns={COLUMNS} showPagination={false} sortable={false} rowKey="application_id" apiData={recent} />
          )}
        </div>
      </PermissionGuard>

      <PermissionGuard permission={PERMISSIONS.JOB_VIEW} isComponent>
        <div className="rounded-xl border border-border-primary bg-background-secondary">
          <SectionHeader title="Top Open Jobs" right={<ViewAllBtn onClick={() => navigate("/admin/jobs")} />} />

          {loading ? (
            <div className="p-4">
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="grid grid-cols-6 items-center gap-4 py-1.5">
                    <div className="h-3.5 w-32 rounded bg-gray-200/60 animate-pulse" />
                    <div className="h-3.5 w-20 rounded bg-gray-200/60 animate-pulse" />
                    <div className="h-3.5 w-16 rounded bg-gray-200/60 animate-pulse" />
                    <div className="h-3.5 w-12 rounded bg-gray-200/60 animate-pulse" />
                    <div className="h-3.5 w-10 rounded bg-gray-200/60 animate-pulse" />
                    <div className="h-5 w-18 rounded-full bg-gray-200/50 animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <DataTable
              className="rounded-none"
              key="jobs-table"
              columns={JOB_COLUMNS}
              showPagination={false}
              sortable={false}
              apiData={jobRows}
              onRowClick={(row) => navigate(`/admin/job-detail/${row.id}`)}
            />
          )}
        </div>
      </PermissionGuard>
    </div>
  );
};

export default Dashboard;
